import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/ui/button';
import Spinner from '../components/ui/spinner';
import TestCoverageTable from './TestCoverageTable';
import TestScenariosList from './TestScenariosList';
import TestScenarioDetail from './TestScenarioDetail';
import GlobalTestsGraph from './GlobalTestsGraph';
import apiClient from '../utils/apiClient';
import { getTestsCount } from '../utils/testUtils';
import './GlobalTestsSection.css';

const GlobalTestsSection = ({
  nodes,
  selectedNode,
  testScenarios,
  setTestScenarios,
  selectedTestScenario,
  setSelectedTestScenario,
  duplicateTestIds,
  extractStepsByType,
  removeDuplicateTest,
  handleUpdateScenario,
  handleOpenManualTestCreator,
  handleOpenTestGeneration,
  generateTests,
  fetchNodes,
  addPendingNode,
  loading,
  setLoading,
  autoLoadTests,
  setAutoLoadTests
}) => {
  const { t } = useTranslation();
  // State to manage the coverage accordion open/close
  const [isCoverageExpanded, setIsCoverageExpanded] = useState(false);
  
  // State for table filters
  const [sortBy, setSortBy] = useState('name'); // 'name', 'type', 'status', 'testCount'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' ou 'desc'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'tested', 'untested'
  
  // Ref pour scroller vers le nœud sélectionné
  const tableBodyRef = useRef(null);

  // Effet pour auto-charger les tests quand la modal s'ouvre via NodeDetailsPanel
  useEffect(() => {
    if (autoLoadTests) {
      // Déclencher le chargement automatique des tests du nœud sélectionné
      // (équivalent au bouton "Voir les tests de nodeName")
      handleLoadNodeTests();
      
      // Scroller vers le nœud sélectionné dans la liste après un petit délai
      // pour que le rendu soit terminé
      setTimeout(() => {
        scrollToSelectedNode();
      }, 300);
      
      // Réinitialiser le flag d'auto-chargement
      if (setAutoLoadTests) {
        setAutoLoadTests(false);
      }
    }
  }, [autoLoadTests]); // Déclenché quand autoLoadTests change

  // Fonction pour scroller vers le nœud sélectionné dans la liste
  const scrollToSelectedNode = () => {
    if (!selectedNode || !tableBodyRef.current) return;

    const tableRows = tableBodyRef.current.querySelectorAll('.table-row');
    const filteredNodes = getFilteredAndSortedNodes();
    
    // Trouver l'index du nœud sélectionné dans la liste filtrée
    const nodeIndex = filteredNodes.findIndex(node => node.name === selectedNode.name);
    
    if (nodeIndex !== -1 && tableRows[nodeIndex]) {
      // Scroller vers le nœud avec un effet smooth
      tableRows[nodeIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'start', // Positionner le nœud vers le haut pour voir plus de contexte en dessous
        inline: 'nearest'
      });
      
      // Optionnel : ajouter un effet highlight temporaire
      tableRows[nodeIndex].classList.add('highlighted-node');
      setTimeout(() => {
        tableRows[nodeIndex].classList.remove('highlighted-node');
      }, 2000);
    }
  };

  // Coverage statistics calculation
  const getCoverageStats = () => {
    if (!nodes) return { covered: 0, total: 0, percentage: 0, totalTests: 0 };
    
    // Uses the same logic as TestCoverageTable
    const coveredNodes = nodes.filter(node => getTestsCount(node.testsJson) > 0);
    
    const total = nodes.length;
    const covered = coveredNodes.length;
    const percentage = total > 0 ? Math.round((covered / total) * 100) : 0;
    
    // Counts the total of all tests in testsJson
    const totalTests = nodes.reduce((acc, node) => {
      return acc + getTestsCount(node.testsJson);
    }, 0);
    
    return { covered, total, percentage, totalTests };
  };

  const coverageStats = getCoverageStats();
  const coveragePercent = coverageStats.total > 0 ? Math.round((coverageStats.covered / coverageStats.total) * 100) : 0;

  // Fonction pour trier et filtrer les nœuds
  const getFilteredAndSortedNodes = () => {
    if (!nodes) return [];
    
    let filteredNodes = [...nodes];
    
    // Filtrage par statut
    if (filterStatus === 'tested') {
      filteredNodes = filteredNodes.filter(node => node.testsJson && node.testsJson.length > 0);
    } else if (filterStatus === 'untested') {
      filteredNodes = filteredNodes.filter(node => !node.testsJson || node.testsJson.length === 0);
    }
    
    // Tri
    filteredNodes.sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'name':
          valueA = a.name?.toLowerCase() || '';
          valueB = b.name?.toLowerCase() || '';
          break;
        case 'type':
          valueA = a.type?.toLowerCase() || '';
          valueB = b.type?.toLowerCase() || '';
          break;
        case 'status':
          valueA = (a.testsJson && a.testsJson.length > 0) ? 'tested' : 'untested';
          valueB = (b.testsJson && b.testsJson.length > 0) ? 'tested' : 'untested';
          break;
        case 'testCount':
          valueA = a.testsJson ? a.testsJson.length : 0;
          valueB = b.testsJson ? b.testsJson.length : 0;
          break;
        default:
          return 0;
      }
      
      if (sortBy === 'testCount') {
        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
    
    return filteredNodes;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleLoadAllTests = () => {
    setLoading(true);
    apiClient.get(`/tests`)
      .then((response) => {
        setTestScenarios(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des tests:", error);
        setLoading(false);
      });
  };

  const handleLoadNodeTests = () => {
    if (!selectedNode) return;
    
    setLoading(true);
    apiClient.get(`/node/${selectedNode.name}/tests`)
      .then((response) => {
        setTestScenarios(
          response.data.map((test) => ({
            ...test,
            nodeName: selectedNode.name,
          }))
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des tests:", error);
        setLoading(false);
      });
  };

  return (
    <div id="section-tests-global" className="global-tests-section">
      {/* Header moderne avec statistiques */}
      <div className="global-tests-header">
        <div className="header-content">
          <div className="section-title-area">
            <div className="title-icon">
              🧪
            </div>
            <div className="title-text">
              <h2 className="main-title">{t('globalTestsSection.title')}</h2>
              <p className="subtitle">{t('globalTestsSection.subtitle')}</p>
            </div>
          </div>
          <div className="global-tests-stats">
            <div className="stat-card">
              <span className="stat-value">{nodes?.length || 0}</span>
              <span className="stat-label">{t('globalTestsSection.stats.nodes')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">
                {coverageStats.totalTests}
              </span>
              <span className="stat-label">{t('globalTestsSection.stats.tests')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">
                {coverageStats.covered}
              </span>
              <span className="stat-label">{t('globalTestsSection.stats.nodesWithTests')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Body avec actions principales */}
      <div className="global-tests-body">
        {/* Cards d'actions */}
        <div className="global-test-actions">
          <div className="global-test-action-card create-test">
            <span className="card-icon">✏️</span>
            <h3 className="card-title">{t('globalTestsSection.createTest.title')}</h3>
            <p className="card-description">
              {t('globalTestsSection.createTest.description')}
            </p>
            <button 
              className="action-card-button"
              onClick={handleOpenManualTestCreator}
              title={t('globalTestsSection.createTest.tooltip')}
            >
              <span className="button-icon">➕</span>
              {t('globalTestsSection.createTest.button')}
            </button>
          </div>

          <div className="global-test-action-card view-graph">
            <span className="card-icon">📊</span>
            <h3 className="card-title">{t('globalTestsSection.testVisualization.title')}</h3>
            <p className="card-description">
              {t('globalTestsSection.testVisualization.description')}
            </p>
            <button 
              className="action-card-button"
              onClick={handleLoadAllTests}
              disabled={loading}
            >
              {loading ? (
                <Spinner />
              ) : (
                <>
                  <span className="button-icon">🧪</span>
                  {t('globalTestsSection.testVisualization.button')}
                </>
              )}
            </button>
          </div>

          <div className="global-test-action-card ai-generate">
            <span className="card-icon">🤖</span>
            <h3 className="card-title">{t('globalTestsSection.aiGenerate.title')}</h3>
            <p className="card-description">
              {t('globalTestsSection.aiGenerate.description')}
            </p>
            <button 
              className="action-card-button"
              onClick={handleOpenTestGeneration}
              disabled={!selectedNode || loading}
              title={!selectedNode ? t('globalTestsSection.aiGenerate.selectNodeTooltip') : t('globalTestsSection.aiGenerate.generateTooltip')}
            >
              <span className="button-icon">🚀</span>
              {selectedNode ? t('globalTestsSection.aiGenerate.buttonWithNode', { nodeName: selectedNode.name }) : t('globalTestsSection.aiGenerate.buttonNoNode')}
            </button>
          </div>

          {selectedNode && (
            <div className="global-test-action-card add-scenario">
              <span className="card-icon">🎯</span>
              <h3 className="card-title">{t('featureManager.nodeTests.title')}</h3>
              <p className="card-description">
                {t('featureManager.nodeTests.description', { nodeName: selectedNode.name })}
              </p>
              <button 
                className="action-card-button"
                onClick={handleLoadNodeTests}
                disabled={loading}
              >
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <span className="button-icon">🎯</span>
                    {t('globalTestsSection.nodeTests.button', { nodeName: selectedNode.name })}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Section tableau de couverture - Accordéon */}
        <div className="coverage-accordion-section">
          <button 
            className={`coverage-accordion-header ${isCoverageExpanded ? 'expanded' : ''}`}
            onClick={() => setIsCoverageExpanded(!isCoverageExpanded)}
          >
            <div className="accordion-title-area">
              <span className="accordion-icon">📋</span>
              <div className="accordion-title-text">
                <h3 className="accordion-title">{t('globalTestsSection.testCoverage.title')}</h3>
                <p className="accordion-subtitle">
                  {t('globalTestsSection.coverageSubtitle')}
                </p>
              </div>
            </div>
            
            <div className="accordion-stats-preview">
              <span className={`expand-arrow ${isCoverageExpanded ? 'rotated' : ''}`}>
                ▼
              </span>
            </div>
          </button>
          
          <div className={`coverage-accordion-content ${isCoverageExpanded ? 'expanded' : ''}`}>
            <div className="accordion-content-wrapper">
              <div className="coverage-detail-view">
                {/* Résumé compact */}
                <div className="coverage-summary">
                  <div className="summary-stats">
                    <div className="stat-box covered">
                      <span className="stat-number">{coverageStats.covered}</span>
                      <span className="stat-label">{t('globalTestsSection.stats.nodesWithTests')}</span>
                    </div>
                    <div className="stat-box uncovered">
                      <span className="stat-number">{coverageStats.total - coverageStats.covered}</span>
                      <span className="stat-label">{t('globalTestsSection.filters.withoutTests')}</span>
                    </div>
                    <div className="stat-box total">
                      <span className="stat-number">{coverageStats.totalTests}</span>
                      <span className="stat-label">{t('globalTestsSection.stats.tests')}</span>
                    </div>
                    <div className="stat-box percentage">
                      <span className="stat-number">{coveragePercent}%</span>
                      <span className="stat-label">{t('globalTestsSection.stats.coverage')}</span>
                    </div>
                  </div>
                  
                  <div className="coverage-description">
                    <p className="coverage-text_2" dangerouslySetInnerHTML={{ __html: t('globalTestsSection.coverageDescription', { covered: coverageStats.covered, total: coverageStats.total, percentage: coveragePercent }) }} />
                  </div>
                </div>

                {/* Tableau modernisé */}
                <div className="modern-coverage-table">
                  {/* Barre de filtres */}
                  <div className="table-filters">
                    <div className="filter-group">
                      <label className="filter-label">{t('globalTestsSection.filters.labelFilterByStatus')}</label>
                      <select 
                        className="filter-select"
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">{t('globalTestsSection.filters.allNodes')}</option>
                        <option value="tested">{t('globalTestsSection.filters.withTests')}</option>
                        <option value="untested">{t('globalTestsSection.filters.withoutTests')}</option>
                      </select>
                    </div>
                    
                    <div className="filter-group">
                      <label className="filter-label">{t('globalTestsSection.filters.labelSortBy')}</label>
                      <select 
                        className="filter-select"
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="name">{t('globalTestsSection.table.nodeName')}</option>
                        <option value="type">{t('globalTestsSection.table.type')}</option>
                        <option value="status">{t('globalTestsSection.table.status')}</option>
                        <option value="testCount">{t('globalTestsSection.table.testCount')}</option>
                      </select>
                    </div>
                    
                    <button 
                      className="sort-order-btn"
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      title={t('globalTestsSection.sortTitle', { order: sortOrder === 'asc' ? t('globalTestsSection.ascending') : t('globalTestsSection.descending') })}
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>

                  <div className="table-header">
                    <div className="header-cell node-name2" onClick={() => handleSort('name')}>
                      {t('globalTestsSection.table.nodeName')} {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                    <div className="header-cell node-type" onClick={() => handleSort('type')}>
                      {t('globalTestsSection.table.type')} {sortBy === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                    <div className="header-cell test-status" onClick={() => handleSort('status')}>
                      {t('globalTestsSection.table.status')} {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                    <div className="header-cell test-count" onClick={() => handleSort('testCount')}>
                      {t('globalTestsSection.table.testCount')} {sortBy === 'testCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </div>
                  
                  <div className="table-body" ref={tableBodyRef}>
                    {getFilteredAndSortedNodes().map((node, index) => {
                      const hasTests = node.testsJson && node.testsJson.length > 0;
                      const nodeTestsCount = node.testsJson ? node.testsJson.length : 0;
                      
                      return (
                        <div key={index} className={`table-row ${hasTests ? 'has-tests' : 'no-tests'}`}>
                          <div className="cell node-name">
                            <div className="node-info-cell">
                              <span className="node-icon">
                                {hasTests ? '✅' : '❌'}
                              </span>
                              <span className="node-name-text" title={node.name}>
                                {node.name}
                              </span>
                            </div>
                          </div>
                          
                          <div className="cell node-type">
                            {node.type && (
                              <span className="type-badge">{node.type}</span>
                            )}
                          </div>
                          
                          <div className="cell test-status">
                            <span className={`status-badge ${hasTests ? 'tested' : 'untested'}`}>
                              {hasTests ? t('globalTestsSection.tested') : t('globalTestsSection.notTested')}
                            </span>
                          </div>
                          
                          <div className="cell test-count">
                            {nodeTestsCount > 0 && (
                              <div className="count-display">
                                <span className="count-number">{nodeTestsCount}</span>
                                <span className="count-text">test{nodeTestsCount > 1 ? 's' : ''}</span>
                              </div>
                            )}
                            {nodeTestsCount === 0 && (
                              <span className="no-tests-text">{t('globalTestsSection.noTests')}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section des scénarios de test */}
        {testScenarios && testScenarios.length > 0 && (
          <div className="test-scenarios-section">
            <TestScenariosList
              testScenarios={testScenarios}
              duplicateTestIds={duplicateTestIds}
              extractStepsByType={extractStepsByType}
              onRemoveDuplicate={removeDuplicateTest}
              onSelectScenario={setSelectedTestScenario}
              selectedNode={selectedNode}
              loading={loading}
            />
          </div>
        )}

        {/* {t('featureManager.selectedScenarioDetail')} */}
        {selectedTestScenario && (
          <TestScenarioDetail
            scenario={selectedTestScenario}
            extractStepsByType={extractStepsByType}
            onClose={() => setSelectedTestScenario(null)}
            onUpdateScenario={handleUpdateScenario}
          />
        )}

        {/* Graphique global des tests */}
        <GlobalTestsGraph testScenarios={testScenarios} nodes={nodes} />
      </div>
    </div>
  );
};

export default GlobalTestsSection;
